import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LOADING_STATE } from "types/general";
import { initialState, RESEARCH_SLICE_NAME } from "./constants";
import {
  addWorkItemNote,
  assignWorkItem,
  cancelDemand,
  claimWorkItem,
  fetchResearchProductivity,
  fetchResearchClientProgress,
  fetchResearchPropertyHistory,
  fetchResearchReferenceData,
  fetchResearchSummary,
  fetchResearchWorkItemPath,
  fetchResearchWorkItems,
  previewIntake,
  submitIntake,
  transitionWorkItem,
  unclaimWorkItem,
  updateDemandMetadata,
} from "./controllers";
import {
  IResearchCompContext,
  IResearchState,
  IResearchWorkItem,
} from "./types";

const replaceWorkItem = (
  state: IResearchState,
  workItem: IResearchWorkItem
) => {
  const index = state.workItems.data.findIndex(
    (item) => item.id === workItem.id
  );
  if (index >= 0) {
    state.workItems.data[index] = workItem;
  }
  if (state.selectedPath.data?.workItem.id === workItem.id) {
    state.selectedPath.data.workItem = workItem;
  }
};

const researchSlice = createSlice({
  name: RESEARCH_SLICE_NAME,
  initialState,
  reducers: {
    clearIntakePreview: (state) => {
      state.intake.preview = undefined;
      state.intake.loadingState = LOADING_STATE.idle;
    },
    clearSelectedPath: (state) => {
      state.selectedPath = initialState.selectedPath;
      state.propertyHistory = initialState.propertyHistory;
    },
    setResearchCompContext: (
      state,
      { payload }: PayloadAction<IResearchCompContext | undefined>
    ) => {
      state.compContext = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchResearchReferenceData.pending, (state) => {
        state.referenceData.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchReferenceData.rejected, (state) => {
        state.referenceData.loadingState = LOADING_STATE.rejected;
      })
      .addCase(fetchResearchReferenceData.fulfilled, (state, { payload }) => {
        state.referenceData.data = payload;
        state.referenceData.loadingState = LOADING_STATE.fulfilled;
      })
      .addCase(fetchResearchSummary.pending, (state, { meta }) => {
        state.summary.currentRequestId = meta.requestId;
        state.summary.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchSummary.rejected, (state, { meta }) => {
        if (state.summary.currentRequestId !== meta.requestId) return;
        state.summary.currentRequestId = undefined;
        state.summary.loadingState = LOADING_STATE.rejected;
      })
      .addCase(fetchResearchSummary.fulfilled, (state, { payload, meta }) => {
        if (state.summary.currentRequestId !== meta.requestId) return;
        state.summary.currentRequestId = undefined;
        state.summary.data = payload;
        state.summary.loadingState = LOADING_STATE.fulfilled;
      })
      .addCase(fetchResearchWorkItems.pending, (state, { meta }) => {
        state.workItems.currentRequestId = meta.requestId;
        state.workItems.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchWorkItems.rejected, (state, { meta }) => {
        if (state.workItems.currentRequestId !== meta.requestId) return;
        state.workItems.currentRequestId = undefined;
        state.workItems.loadingState = LOADING_STATE.rejected;
      })
      .addCase(fetchResearchWorkItems.fulfilled, (state, { payload, meta }) => {
        if (state.workItems.currentRequestId !== meta.requestId) return;
        state.workItems.currentRequestId = undefined;
        state.workItems.data = payload;
        state.workItems.loadingState = LOADING_STATE.fulfilled;
      })
      .addCase(fetchResearchClientProgress.pending, (state, { meta }) => {
        state.clientProgress.currentRequestId = meta.requestId;
        state.clientProgress.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchClientProgress.rejected, (state, { meta }) => {
        if (state.clientProgress.currentRequestId !== meta.requestId) return;
        state.clientProgress.currentRequestId = undefined;
        state.clientProgress.loadingState = LOADING_STATE.rejected;
      })
      .addCase(
        fetchResearchClientProgress.fulfilled,
        (state, { payload, meta }) => {
          if (state.clientProgress.currentRequestId !== meta.requestId) return;
          state.clientProgress.currentRequestId = undefined;
          state.clientProgress.data = payload;
          state.clientProgress.loadingState = LOADING_STATE.fulfilled;
        }
      )
      .addCase(fetchResearchWorkItemPath.pending, (state, { meta }) => {
        state.selectedPath.currentRequestId = meta.requestId;
        state.selectedPath.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchWorkItemPath.rejected, (state, { meta }) => {
        if (state.selectedPath.currentRequestId !== meta.requestId) return;
        state.selectedPath.currentRequestId = undefined;
        state.selectedPath.loadingState = LOADING_STATE.rejected;
      })
      .addCase(
        fetchResearchWorkItemPath.fulfilled,
        (state, { payload, meta }) => {
          if (state.selectedPath.currentRequestId !== meta.requestId) return;
          state.selectedPath.currentRequestId = undefined;
          state.selectedPath.data = payload;
          state.selectedPath.loadingState = LOADING_STATE.fulfilled;
        }
      )
      .addCase(fetchResearchPropertyHistory.pending, (state, { meta }) => {
        state.propertyHistory.currentRequestId = meta.requestId;
        state.propertyHistory.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchPropertyHistory.rejected, (state, { meta }) => {
        if (state.propertyHistory.currentRequestId !== meta.requestId) return;
        state.propertyHistory.currentRequestId = undefined;
        state.propertyHistory.loadingState = LOADING_STATE.rejected;
      })
      .addCase(
        fetchResearchPropertyHistory.fulfilled,
        (state, { payload, meta }) => {
          if (state.propertyHistory.currentRequestId !== meta.requestId) return;
          state.propertyHistory.currentRequestId = undefined;
          state.propertyHistory.data = payload;
          state.propertyHistory.loadingState = LOADING_STATE.fulfilled;
        }
      )
      .addCase(fetchResearchProductivity.pending, (state, { meta }) => {
        state.productivity.currentRequestId = meta.requestId;
        state.productivity.loadingState = LOADING_STATE.pending;
      })
      .addCase(fetchResearchProductivity.rejected, (state, { meta }) => {
        if (state.productivity.currentRequestId !== meta.requestId) return;
        state.productivity.currentRequestId = undefined;
        state.productivity.loadingState = LOADING_STATE.rejected;
      })
      .addCase(
        fetchResearchProductivity.fulfilled,
        (state, { payload, meta }) => {
          if (state.productivity.currentRequestId !== meta.requestId) return;
          state.productivity.currentRequestId = undefined;
          state.productivity.data = payload;
          state.productivity.loadingState = LOADING_STATE.fulfilled;
        }
      )
      .addCase(previewIntake.pending, (state) => {
        state.intake.loadingState = LOADING_STATE.pending;
      })
      .addCase(previewIntake.rejected, (state) => {
        state.intake.loadingState = LOADING_STATE.rejected;
      })
      .addCase(previewIntake.fulfilled, (state, { payload }) => {
        state.intake.preview = payload;
        state.intake.loadingState = LOADING_STATE.fulfilled;
      })
      .addCase(submitIntake.pending, (state) => {
        state.intake.loadingState = LOADING_STATE.pending;
      })
      .addCase(submitIntake.rejected, (state) => {
        state.intake.loadingState = LOADING_STATE.rejected;
      })
      .addCase(submitIntake.fulfilled, (state) => {
        state.intake = initialState.intake;
      })
      .addCase(cancelDemand.fulfilled, (state, { payload, meta }) => {
        // Reads started before this deletion must not restore removed work or points.
        state.workItems.currentRequestId = undefined;
        state.workItems.loadingState = LOADING_STATE.idle;
        state.workItems.data = state.workItems.data.filter(
          (item) => item.id !== payload.workItemId
        );
        if (payload.removed) {
          state.selectedPath = initialState.selectedPath;
          if (state.compContext?.workItemId === payload.workItemId) {
            state.compContext = undefined;
          }
        } else if (
          state.selectedPath.data?.workItem.id === payload.workItemId
        ) {
          state.selectedPath.currentRequestId = undefined;
          state.selectedPath.data.workItem.demands =
            state.selectedPath.data.workItem.demands.filter(
              (demand) => demand.id !== meta.arg.demandId
            );
        }
        state.propertyHistory = initialState.propertyHistory;
        state.clientProgress = initialState.clientProgress;
        state.productivity = initialState.productivity;
        state.summary = initialState.summary;
      });

    [
      claimWorkItem,
      unclaimWorkItem,
      transitionWorkItem,
      addWorkItemNote,
      assignWorkItem,
      updateDemandMetadata,
    ].forEach((action) => {
      builder.addCase(action.fulfilled, (state, { payload }) => {
        replaceWorkItem(state, payload);
      });
    });
  },
});

export const { clearIntakePreview, clearSelectedPath, setResearchCompContext } =
  researchSlice.actions;

export default researchSlice;
